import { useState } from 'react'
import { Users, Shield, AlertCircle, CheckCircle, Save, X, Search, Plus } from 'lucide-react'

export default function AdminAccessControlPanel() {
  const [searchTerm, setSearchTerm] = useState('')
  const [activeTab, setActiveTab] = useState('users')

  const [users] = useState([
    {
      id: 1,
      name: 'Admin User',
      email: 'admin@cybertection.com',
      role: 'Super Admin',
      status: 'Active',
      joinDate: '2024-01-01',
    },
    {
      id: 2,
      name: 'John Smith',
      email: 'john@cybertection.com',
      role: 'Help Desk Manager',
      status: 'Active',
      joinDate: '2024-02-10',
    },
    {
      id: 3,
      name: 'Sarah Johnson',
      email: 'sarah@cybertection.com',
      role: 'Marketing Manager',
      status: 'Active',
      joinDate: '2024-02-15',
    },
  ])

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">🔐 Admin Access Control Panel</h1>
        <p className="text-blue-200">Manage user permissions and feature access</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
          <p className="text-blue-300/70 text-sm mb-1">Total Users</p>
          <p className="text-3xl font-bold text-blue-400">{users.length}</p>
        </div>
        <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30 p-6">
          <p className="text-green-300/70 text-sm mb-1">Active Users</p>
          <p className="text-3xl font-bold text-green-400">{users.filter(u => u.status === 'Active').length}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-lg border border-purple-500/30 p-6">
          <p className="text-purple-300/70 text-sm mb-1">Roles</p>
          <p className="text-3xl font-bold text-purple-400">6</p>
        </div>
        <div className="bg-gradient-to-br from-orange-600/20 to-red-600/20 rounded-lg border border-orange-500/30 p-6">
          <p className="text-orange-300/70 text-sm mb-1">Features</p>
          <p className="text-3xl font-bold text-orange-400">40+</p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-blue-500/30">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'users'
              ? 'text-blue-300 border-b-2 border-blue-500'
              : 'text-blue-400/60 hover:text-blue-300'
          }`}
        >
          User Management
        </button>
        <button
          onClick={() => setActiveTab('permissions')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'permissions'
              ? 'text-blue-300 border-b-2 border-blue-500'
              : 'text-blue-400/60 hover:text-blue-300'
          }`}
        >
          Permissions
        </button>
      </div>

      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-3 text-blue-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
              />
            </div>
            <button className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-green-500/50 transition-all flex items-center space-x-2">
              <Plus size={18} />
              <span>Add User</span>
            </button>
          </div>

          {filteredUsers.map((user) => (
            <div key={user.id} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-lg border border-blue-500/30 p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <p className="font-bold text-white">{user.name}</p>
                    <span className="px-3 py-1 text-xs font-bold rounded-full bg-blue-500/30 text-blue-300">
                      {user.role}
                    </span>
                    <span className="px-3 py-1 text-xs font-bold rounded-full bg-green-500/30 text-green-300">
                      {user.status}
                    </span>
                  </div>
                  <p className="text-sm text-blue-300/70">{user.email}</p>
                  <p className="text-xs text-blue-400/70 mt-1">Joined {user.joinDate}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'permissions' && (
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-lg border border-blue-500/30 p-6">
          <p className="text-white mb-4">Permission management features coming soon. Use User Management tab to assign roles.</p>
        </div>
      )}

      <div className="bg-blue-500/10 rounded-lg border border-blue-500/30 p-6">
        <h3 className="text-lg font-bold text-blue-300 mb-3">🔒 Admin Security</h3>
        <ul className="space-y-2 text-sm text-blue-200">
          <li>✓ Complete user and permission management</li>
          <li>✓ Role-based access control (RBAC)</li>
          <li>✓ Audit logging of all access changes</li>
          <li>✓ Feature-level permission control</li>
        </ul>
      </div>
    </div>
  )
}
